import { useCallback, useEffect, useState } from 'react';
import PageHeader from '../components/common/PageHeader';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import {
  NEUTRAL,
  sourceColors,
  sourceLabel,
} from '../components/linkbuilder/linkSourceColors';
import type { LinkMap } from '../types';

/**
 * The link-map list — the only cross-map surface.
 *
 * A map is an ARTIFACT, not a setting: named, kept, reopened, run again. That
 * is why this is a list of things you have made rather than a form. Maps are
 * independent of each other and of any project; a page may belong to several,
 * and at deploy its links are the union of all of them.
 */

function statusChip(map: LinkMap) {
  if (map.status === 'RELINKING') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
        <svg
          className="w-3 h-3 animate-spin"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
        >
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
        </svg>
        Relinking
      </span>
    );
  }
  if (map.status === 'FAILED') {
    return (
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300"
        title={map.last_error || undefined}
      >
        Last run failed
      </span>
    );
  }
  return null;
}

export default function LinkBuilder() {
  const navigate = useNavigate();
  const [maps, setMaps] = useState<LinkMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const load = useCallback(async () => {
    try {
      setMaps(await api.listLinkMaps());
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load link maps');
    } finally {
      // In the finally block: an early return that leaves `loading` true is how
      // a screen ends up saying "Loading…" forever after one failed request.
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const created = await api.createLinkMap({ name });
      // Straight into the new map. It has no sources yet, and the workspace is
      // where you add them — bouncing back to a list with one more empty row
      // makes the user find the thing they just made.
      navigate(`/link-builder/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create the link map');
      setCreating(false);
    }
  };

  const handleDelete = async (map: LinkMap) => {
    if (
      !window.confirm(
        `Delete "${map.name}"?\n\n` +
          `${map.edge_count} link${map.edge_count === 1 ? '' : 's'} ` +
          `will be removed. Your pages and subjects are not affected - only ` +
          `the links this map drew between them.\n\nThis cannot be undone.`,
      )
    )
      return;
    try {
      await api.deleteLinkMap(map.id);
      setMaps((prev) => prev.filter((m) => m.id !== map.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete the link map');
    }
  };

  return (
    <div>
      <PageHeader
        title="Link Builder"
        subtitle="Pick subjects, see all their pages in one place, and link them - including across different websites. Links are added when a page is published, so you can change them and redeploy without rewriting anything."
      />

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleCreate} className="mb-6 flex gap-2 max-w-xl">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New map - e.g. “Outdoor network”"
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
        <button
          type="submit"
          disabled={!newName.trim() || creating}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {creating ? 'Creating…' : 'New map'}
        </button>
      </form>

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : maps.length === 0 ? (
        <div className="border border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            No link maps yet.
          </p>
          {/* Says what a map is FOR, in the terms of the payoff. "Create your
              first link map" would explain nothing to someone who has not been
              told what one does. */}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            A map is a set of links over the pages of one or more subjects. Build one
            over two subjects on different sites to link them together.
          </p>
        </div>
      ) : (
        // A GRID, matching Subjects, Brainstorms and Managed sites. Full-width
        // rows put a two-word name and a chip beside a hundred characters of
        // empty space, and read as a table that has lost its columns.
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {maps.map((map) => {
            const colors = sourceColors(map.scopes);
            return (
              // `group` so the delete button can stay hidden until the card is
              // hovered: on a card it sits inside the thing it destroys, where
              // a permanently visible one is an easy misclick.
              <div
                key={map.id}
                className="group relative flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition"
              >
                <div className="flex items-start gap-2">
                  <Link
                    to={`/link-builder/${map.id}`}
                    // Stretched over the whole card, so the target is the card
                    // rather than the few characters of its name. The delete
                    // button sits above it on the z-axis.
                    className="font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 min-w-0 after:absolute after:inset-0"
                  >
                    {map.name}
                  </Link>
                  <span className="ml-auto shrink-0">{statusChip(map)}</span>
                </div>

                {map.scopes.length > 0 ? (
                  // The sources ALREADY WEARING their colour, so the
                  // colour↔source association is learned here rather than
                  // being met cold in the merged tree.
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {map.scopes.map((scope) => {
                      const c = colors.get(scope.id) ?? NEUTRAL;
                      return (
                        <span
                          key={scope.id}
                          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs ${c.soft} ${c.text}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                          {sourceLabel(scope)}
                        </span>
                      );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    No sources yet - open it to add a subject.
                  </p>
                )}

                {/* `mt-auto` so the footer sits on the bottom edge whatever a
                    card's source list did to its height — grid rows stretch to
                    the tallest, and without this every card's counts land at a
                    different depth. */}
                <p className="mt-auto pt-3 text-xs text-gray-500 dark:text-gray-400">
                  {map.edge_count} link{map.edge_count === 1 ? '' : 's'}
                  {map.last_relinked_at && (
                    <>
                      {' · '}last relinked{' '}
                      {new Date(map.last_relinked_at).toLocaleDateString()}
                    </>
                  )}
                </p>

                <button
                  onClick={() => handleDelete(map)}
                  className="absolute bottom-3 right-3 z-10 p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                  title="Delete this map"
                  aria-label={`Delete ${map.name}`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
